import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { Icon } from '../../components/icons';
import type { UserSettings } from '../../types';

const PROVIDER_LABEL: Record<string, string> = {
  claude: 'Claude (Anthropic)', openai: 'OpenAI', groq: 'Groq', azureopenai: 'Azure OpenAI',
};

export default function MySettingsHub() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const { isAdmin }   = useUserRole();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const navigate       = useNavigate();

  const [userSettings, setUserSettings]       = useState<UserSettings | null>(null);
  const [orgUsesTestLink, setOrgUsesTestLink] = useState(false);

  useEffect(() => {
    api.getUserSettings().then(u => setUserSettings(u ?? {})).catch(() => {});
    // Org settings are only fetched for admins — mirrors the same gating used elsewhere,
    // since a member's read access to org settings isn't guaranteed.
    if (isAdmin) {
      api.getSettings().then(s => setOrgUsesTestLink(s?.targetType === 'testlink')).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const usingPersonalLlmKey = !!userSettings?.llmApiKey && userSettings?.llmUseOrgKey !== true;
  const llmStatus = userSettings
    ? (usingPersonalLlmKey
        ? `Personal key · ${PROVIDER_LABEL[userSettings.llmProvider ?? 'claude'] ?? userSettings.llmProvider}`
        : "Using organisation's key")
    : undefined;

  const usingPersonalGitToken = !!userSettings?.githubToken && userSettings?.githubUseOrgToken !== true;
  const gitStatus = userSettings
    ? (usingPersonalGitToken ? 'Using personal token' : "Using organisation's token")
    : undefined;

  const cards: Array<{ key: string; icon: string; title: string; desc: string; status?: string }> = [
    {
      key: 'llm',
      icon: 'cpu',
      title: 'AI / LLM Configuration',
      desc: 'Your personal LLM provider and API key, used by the portal Generator and Chrome extension.',
      status: llmStatus,
    },
    {
      key: 'git',
      icon: 'git',
      title: 'GitHub Integration',
      desc: 'Your personal GitHub token for the Web Automation Coder\'s "Push to repo" button.',
      status: gitStatus,
    },
  ];

  if (orgUsesTestLink) {
    cards.push({
      key: 'testlink',
      icon: 'clipboard',
      title: 'My TestLink Profile',
      desc: 'The TestLink username test cases you push are authored under.',
      status: userSettings?.testLinkLogin ? `Username: ${userSettings.testLinkLogin}` : 'Using default (admin)',
    });
  }

  return (
    <div className="settings-hub-grid">
      {cards.map(c => (
        <button
          key={c.key}
          type="button"
          className="settings-hub-card"
          onClick={() => navigate(`/settings/profile/${c.key}`)}
        >
          <span className="settings-hub-card-icon"><Icon name={c.icon} size={22} /></span>
          <span className="settings-hub-card-title">{c.title}</span>
          <span className="settings-hub-card-desc">{c.desc}</span>
          <span className="settings-hub-card-status">{c.status ?? ' '}</span>
        </button>
      ))}
    </div>
  );
}
